import React from 'react';
import { Tag, Typography, Space } from 'antd';
import { HeartOutlined, HeartFilled, MessageOutlined } from '@ant-design/icons';
import PriceTag from '../PriceTag/index';
import VoteButtons from '../VoteButtons/index';

const { Text } = Typography;

const CONDITION_LABELS = {
  new: 'New',
  refurbished: 'Refurbished',
  used: 'Used',
  used_open_box: 'Open Box',
  open_box: 'Open Box',
};

function entityLabel(value) {
  if (value == null) return null;
  if (typeof value === 'string') return value || null;
  if (typeof value === 'object') return value.name || null;
  return null;
}

export default function DealCard({ deal, onVote, onSave, compact }) {
  const categoryName = entityLabel(deal.category);
  const merchantName = entityLabel(deal.merchant);

  if (compact) {
    return (
      <div className="df-deal-card" style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        padding: '12px 16px',
        marginBottom: 6,
      }}>
        <div style={{
          width: 52,
          height: 52,
          background: 'linear-gradient(145deg, #EEF3F8, #E0E8F0)',
          borderRadius: 8,
          flexShrink: 0,
          overflow: 'hidden',
        }}>
          {deal.primaryImageUrl && (
            <img
              src={`${deal.primaryImageUrl}?tr=w-104,h-104,fo-auto,f-auto,q-80`}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontSize: 13, fontFamily: 'var(--df-font-body)' }} ellipsis>
            {deal.title}
          </Text>
          <PriceTag price={deal.priceMinor} retailPrice={deal.retailPriceMinor} currency={deal.currency} size="small" />
        </div>
        <VoteButtons up={deal.upvotes} down={deal.downvotes} userVote={deal.userVote} dealId={deal.publicId} onVote={onVote} />
      </div>
    );
  }

  return (
    <article className="df-deal-card">
      <div style={{
        height: 176,
        background: 'linear-gradient(145deg, #EEF3F8 0%, #E0E8F0 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {deal.primaryImageUrl ? (
          <img
            src={`${deal.primaryImageUrl}?tr=w-400,h-176,fo-auto,f-auto,q-80`}
            alt={deal.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Text type="secondary" style={{ fontSize: 13 }}>No image</Text>
        )}
        {deal.discountPercent >= 10 && (
          <span style={{
            position: 'absolute',
            top: 12,
            right: 12,
            margin: 0,
            borderRadius: 8,
            padding: '4px 10px',
            fontSize: 12,
            fontWeight: 700,
            fontFamily: 'var(--df-font-display)',
            background: 'var(--df-accent)',
            color: '#ffffff',
            letterSpacing: '-0.01em',
          }}>
            −{deal.discountPercent}%
          </span>
        )}
      </div>

      <div style={{ padding: '14px 16px 16px' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          {categoryName && (
            <Tag style={{
              fontSize: 11,
              lineHeight: '18px',
              borderRadius: 6,
              border: 'none',
              background: '#EEF3F8',
              color: 'var(--df-text-secondary)',
              margin: 0,
            }}>
              {categoryName}
            </Tag>
          )}
          {deal.condition && deal.condition !== 'new' && (
            <Tag style={{
              fontSize: 11,
              lineHeight: '18px',
              borderRadius: 6,
              border: 'none',
              background: '#F3EDE6',
              color: '#8A5A2B',
              margin: 0,
            }}>
              {CONDITION_LABELS[deal.condition] || deal.condition}
            </Tag>
          )}
          {deal.isFreeShipping && (
            <Tag style={{
              fontSize: 11,
              lineHeight: '18px',
              borderRadius: 6,
              border: 'none',
              background: 'rgba(15, 123, 90, 0.1)',
              color: 'var(--df-success)',
              margin: 0,
            }}>
              Free Shipping
            </Tag>
          )}
        </div>

        <Text
          strong
          style={{
            fontSize: 15,
            lineHeight: 1.35,
            display: 'block',
            marginBottom: 8,
            fontFamily: 'var(--df-font-display)',
            color: 'var(--df-text)',
          }}
          ellipsis={{ rows: 2 }}
        >
          {deal.title}
        </Text>

        <PriceTag price={deal.priceMinor} retailPrice={deal.retailPriceMinor} currency={deal.currency} size="default" />

        {merchantName && (
          <Text style={{ fontSize: 12, display: 'block', marginTop: 8, color: 'var(--df-text-muted)', fontWeight: 500 }}>
            {merchantName}
          </Text>
        )}

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 12,
          paddingTop: 10,
          borderTop: '1px solid var(--df-border-subtle)',
        }}>
          <VoteButtons
            up={deal.upvotes}
            down={deal.downvotes}
            userVote={deal.userVote}
            dealId={deal.publicId}
            onVote={onVote}
          />
          <Space size={14}>
            <span style={{ color: 'var(--df-text-muted)', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MessageOutlined aria-hidden /> {deal.commentCount || 0}
            </span>
            <button
              type="button"
              aria-label={deal.isSaved ? 'Unsave deal' : 'Save deal'}
              aria-pressed={!!deal.isSaved}
              style={{
                color: deal.isSaved ? 'var(--df-error)' : 'var(--df-text-muted)',
                cursor: 'pointer',
                fontSize: 16,
                lineHeight: 1,
                background: 'none',
                border: 'none',
                padding: 8,
                minWidth: 44,
                minHeight: 44,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={(e) => { e.stopPropagation(); onSave && onSave(deal.publicId); }}
            >
              {deal.isSaved ? <HeartFilled /> : <HeartOutlined />}
            </button>
          </Space>
        </div>
      </div>
    </article>
  );
}
